import { useRef, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { formatSAR, formatDate } from "@/lib/api";
import { Printer, Send, X, Download } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_spa-pos-system/artifacts/zpy718ez_Logo%20Java%20Spa.png";
const SPA_PHONE = "+966 55 222 1465";

export default function PayoutReceipt({ payout, therapist, onClose }) {
  const { t, lang } = useLang();
  const sheetRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const receiptNo = (payout.id || "").slice(0, 8).toUpperCase();
  const gaji = payout.gaji || 0;
  const komisi = payout.komisi !== undefined
    ? payout.komisi
    : (payout.gaji === undefined ? payout.amount : 0);  // legacy: amount = komisi
  const bonus = payout.bonus_google_amount || 0;
  const bonusNames = payout.bonus_google_names || "";
  const total = payout.amount || (gaji + komisi + bonus);

  async function buildPdfBlob() {
    const node = sheetRef.current;
    if (!node) return null;
    const canvas = await html2canvas(node, {
      scale: 1.5,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.72);
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    const pageW = pdf.internal.pageSize.getWidth();   // 210mm
    const pageH = pdf.internal.pageSize.getHeight();  // 297mm
    const imgRatio = canvas.height / canvas.width;
    let imgW = pageW;
    let imgH = imgW * imgRatio;
    // Force single page: if too tall, scale down to fit page height
    if (imgH > pageH) {
      imgH = pageH;
      imgW = imgH / imgRatio;
    }
    const x = (pageW - imgW) / 2;
    const y = (pageH - imgH) / 2;
    pdf.addImage(imgData, "JPEG", x, y, imgW, imgH, undefined, "FAST");
    return pdf.output("blob");
  }

  function pdfFilename() {
    const safe = (therapist?.name || payout.therapist_name || "therapist").replace(/\s+/g, "_");
    return `JavaSPA_Receipt_${safe}_${receiptNo}.pdf`;
  }

  async function handleDownload() {
    setBusy(true);
    try {
      const blob = await buildPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = pdfFilename();
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("receipt.payout.pdfFail"));
    } finally {
      setBusy(false);
    }
  }

  function handlePrint() {
    window.print();
  }

  async function handleSendWA() {
    setBusy(true);
    try {
      const blob = await buildPdfBlob();
      const file = new File([blob], pdfFilename(), { type: "application/pdf" });
      const phoneRaw = (therapist?.phone || "").replace(/[^0-9]/g, "");
      const wa = phoneRaw.startsWith("0") ? "62" + phoneRaw.slice(1) : phoneRaw;

      const lines = [
        `*${t("receipt.payout.waLine1")}*`,
        ``,
        `${t("receipt.payout.waGreet")} ${therapist?.name || payout.therapist_name},`,
        t("receipt.payout.waIntro"),
        ``,
        `1. ${t("receipt.payout.waBase")}: ${formatSAR(gaji)}`,
        `2. ${t("receipt.payout.waCommission")}: ${formatSAR(komisi)}`,
        `3. ${t("receipt.payout.waBonus")}: ${formatSAR(bonus)}`,
        `${t("receipt.payout.waTotal")}: *${formatSAR(total)}*`,
        ``,
        `${t("receipt.payout.waDate")}: ${formatDate(payout.paid_at)}`,
        `${t("receipt.payout.waPaidBy")}: ${payout.paid_by_name || "-"}`,
        `${t("receipt.payout.receiptNo")}: #${receiptNo}`,
        ``,
        t("receipt.payout.waThanks"),
      ];
      const msg = lines.join("\n");

      // Try Web Share API (native — attaches PDF directly on mobile)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: t("receipt.payout.shareTitle"),
            text: msg,
          });
          toast.success(t("receipt.payout.shareSuccess"));
          return;
        } catch (err) {
          if (err.name === "AbortError") return;  // user cancelled
          // fall through to fallback
        }
      }

      // Fallback for desktop / unsupported: download PDF + open WA chat
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = pdfFilename();
      a.click();
      URL.revokeObjectURL(url);

      const encoded = encodeURIComponent(msg);
      const waLink = wa ? `https://wa.me/${wa}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
      window.open(waLink, "_blank");
      toast.message(t("receipt.payout.shareFallback"));
    } catch (e) {
      toast.error(t("receipt.payout.shareFail"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="payout-receipt-modal"
    >
      {/* Action bar (hidden when printing) */}
      <div
        className="print-hide sticky top-0 z-[100] bg-charcoal-900/90 backdrop-blur border-b border-gold/20 px-4 py-3 flex flex-wrap items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm text-gold font-medium">
          {t("receipt.payout.title")} · #{receiptNo}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleDownload}
            disabled={busy}
            data-testid="receipt-download-btn"
            className="btn-outline-gold py-2 px-3 text-xs disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> {t("receipt.payout.downloadPdf")}
          </button>
          <button
            onClick={handlePrint}
            data-testid="receipt-print-btn"
            className="btn-outline-gold py-2 px-3 text-xs"
          >
            <Printer className="h-3.5 w-3.5" /> {t("receipt.payout.printBtn")}
          </button>
          <button
            onClick={handleSendWA}
            disabled={busy}
            data-testid="receipt-wa-btn"
            className="btn-leaf py-2 px-3 text-xs disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" /> {busy ? "..." : t("receipt.payout.sendWa")}
          </button>
          <button
            onClick={onClose}
            data-testid="receipt-close-btn"
            className="p-2 rounded-full hover:bg-white/5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* PRINTABLE RECEIPT — A4 single page */}
      <div className="grid place-items-center p-4 md:p-8">
        <div
          ref={sheetRef}
          data-print="report"
          onClick={(e) => e.stopPropagation()}
          dir={lang === "ar" ? "rtl" : "ltr"}
          className="bg-white text-charcoal-900 shadow-2xl"
          style={{
            width: "210mm",
            minHeight: "297mm",
            color: "#1a1a1a",
            padding: "18mm 16mm",
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between pb-5 border-b-2" style={{ borderColor: "#D4AF37" }}>
            <div className="flex items-center gap-4">
              <img
                src={LOGO_URL}
                alt="Java SPA"
                style={{ height: 64, width: 64, objectFit: "contain" }}
                crossOrigin="anonymous"
              />
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#1a1a1a", letterSpacing: 1 }}>
                  JAVA SPA
                </div>
                <div style={{ fontSize: 10, color: "#6DBE45", letterSpacing: 3, textTransform: "uppercase" }}>
                  Riyadh · Luxury Wellness
                </div>
                <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>{SPA_PHONE}</div>
              </div>
            </div>
            <div style={{ textAlign: lang === "ar" ? "start" : "end" }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#999", textTransform: "uppercase" }}>
                {t("receipt.payout.receiptNo")}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginTop: 2, fontFamily: "monospace" }}>
                #{receiptNo}
              </div>
              <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>{formatDate(payout.paid_at)}</div>
            </div>
          </div>

          {/* Title */}
          <div style={{ textAlign: "center", margin: "22px 0 18px" }}>
            <div style={{ fontSize: 10, letterSpacing: 4, color: "#B8901E", textTransform: "uppercase" }}>
              — {t("receipt.payout.subtitle")} —
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 700, marginTop: 8, color: "#1a1a1a" }}>
              {t("receipt.payout.bodyTitle")}
            </h1>
          </div>

          {/* Therapist info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 22 }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#999", textTransform: "uppercase" }}>
                {t("receipt.payout.therapist")}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, marginTop: 3, color: "#1a1a1a" }}>
                {therapist?.name || payout.therapist_name}
              </div>
              {therapist?.phone && (
                <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{therapist.phone}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#999", textTransform: "uppercase" }}>
                {t("receipt.payout.paidBy")}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, marginTop: 3, color: "#1a1a1a" }}>
                {payout.paid_by_name || "—"}
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{formatDate(payout.paid_at)}</div>
            </div>
          </div>

          {/* Breakdown table */}
          <div style={{ border: "1px solid #e2dcb8", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FFF8E1", color: "#B8901E" }}>
                  <th style={{ padding: "10px 14px", textAlign: lang === "ar" ? "end" : "start", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>#</th>
                  <th style={{ padding: "10px 14px", textAlign: lang === "ar" ? "end" : "start", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>{t("receipt.payout.tableComponent")}</th>
                  <th style={{ padding: "10px 14px", textAlign: lang === "ar" ? "start" : "end", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>{t("receipt.payout.tableAmount")}</th>
                </tr>
              </thead>
              <tbody>
                {gaji > 0 && (
                  <tr style={{ borderTop: "1px solid #f0e9c8" }}>
                    <td style={{ padding: "12px 14px", color: "#999", fontWeight: 600 }}>1</td>
                    <td style={{ padding: "12px 14px", color: "#1a1a1a", fontWeight: 500 }}>{t("receipt.payout.compBase")}</td>
                    <td style={{ padding: "12px 14px", textAlign: lang === "ar" ? "start" : "end", color: "#1a1a1a", fontWeight: 600 }}>
                      {formatSAR(gaji)}
                    </td>
                  </tr>
                )}
                {komisi > 0 && (
                  <tr style={{ borderTop: "1px solid #f0e9c8" }}>
                    <td style={{ padding: "12px 14px", color: "#999", fontWeight: 600 }}>{gaji > 0 ? 2 : 1}</td>
                    <td style={{ padding: "12px 14px", color: "#1a1a1a", fontWeight: 500 }}>{t("receipt.payout.compCommission")}</td>
                    <td style={{ padding: "12px 14px", textAlign: lang === "ar" ? "start" : "end", color: "#1a1a1a", fontWeight: 600 }}>
                      {formatSAR(komisi)}
                    </td>
                  </tr>
                )}
                {bonus > 0 && (
                  <tr style={{ borderTop: "1px solid #f0e9c8" }}>
                    <td style={{ padding: "12px 14px", color: "#999", fontWeight: 600, verticalAlign: "top" }}>
                      {(gaji > 0 ? 1 : 0) + (komisi > 0 ? 1 : 0) + 1}
                    </td>
                    <td style={{ padding: "12px 14px", color: "#1a1a1a", fontWeight: 500, verticalAlign: "top" }}>
                      {t("receipt.payout.compBonus")}
                      {bonusNames && (
                        <div style={{ fontSize: 10, color: "#666", marginTop: 4, fontStyle: "italic", fontWeight: 400 }}>
                          {t("receipt.payout.namesMentioned")}: {bonusNames}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", textAlign: lang === "ar" ? "start" : "end", color: "#1a1a1a", fontWeight: 600, verticalAlign: "top" }}>
                      {formatSAR(bonus)}
                    </td>
                  </tr>
                )}
                <tr style={{ background: "#D4AF37", color: "#fff" }}>
                  <td colSpan={2} style={{ padding: "14px", fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                    {t("receipt.payout.totalReceived")}
                  </td>
                  <td style={{ padding: "14px", textAlign: lang === "ar" ? "start" : "end", fontSize: 20, fontWeight: 800, letterSpacing: 1 }}>
                    {formatSAR(total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {payout.notes && (
            <div style={{ borderInlineStart: "3px solid #6DBE45", paddingInlineStart: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: "#999", textTransform: "uppercase" }}>{t("receipt.payout.notes")}</div>
              <div style={{ fontSize: 12, fontStyle: "italic", color: "#444", marginTop: 4 }}>
                &ldquo;{payout.notes}&rdquo;
              </div>
            </div>
          )}

          {/* Thank you */}
          <div style={{ textAlign: "center", fontSize: 12, fontStyle: "italic", color: "#6DBE45", margin: "20px 0 16px" }}>
            {t("receipt.payout.thanks")} 🙏
          </div>

          {/* Footer */}
          <div style={{ textAlign: "center", paddingTop: 14, borderTop: "1px solid #e6e2d4" }}>
            <div style={{ fontSize: 10, color: "#999" }}>
              Java SPA · Riyadh, Kingdom of Saudi Arabia · {SPA_PHONE}
            </div>
            <div style={{ fontSize: 9, color: "#bbb", marginTop: 3 }}>
              {t("receipt.payout.generatedOn")} {new Date().toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
